import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Planogramme } from "../../../models/Planogramme";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { getModalStyle } from "../../modalStyle";
import GenericModal from "../modals/GenericModal";
import PTRModal from "../modals/PTRModal";
import {
  useGetEnsembleQuery,
  useGetMagasinsQuery,
  useGetPlanogrammeProduitsQuery,
  useUpdatePlanogrammeMutation,
  useUpdatePlanogrammeProduitsMutation,
} from "../../../redux/features/cache";
import { addRequestToSend } from "../../../redux/features/appStateSlice";
import { useDeletePlanogrammeMutation } from "../../../redux/features/cache";
import DeleteIcon from "@mui/icons-material/Delete";
import { padZerosLeft } from "../../../commons/helpers/StringHelper";
import { PTR_LENGTH } from "../../../constante/produits";
import { ProduitQuantite } from "../../../models/ProduitQuantite";
import PlanoProduitsModal from "../modals/PlanoProduitsModal";
import { RequestToSend } from "../../../models/RequestToSend";

export interface IFormEditMagasinProduit {
  index: number;
  data: Planogramme;
  setUnsaved: Dispatch<SetStateAction<boolean>>;
  listChecked: Planogramme[];
}

function FormEditMagasinProduit({
  listChecked,
  data,
  setUnsaved,
}: IFormEditMagasinProduit) {
  const [open, setOpen] = useState(false);
  const [showModalPlanoProduits, setShowModalPlanoProduits] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [deletePlanogramme] = useDeletePlanogrammeMutation();
  const [updatePlanogrammeProduits] = useUpdatePlanogrammeProduitsMutation();
  const handleClose = () => {
    setOpen(false);
  };
  const { data: magasins = [] } = useGetMagasinsQuery({
    banniere: "",
    treeView: false,
  });
  const { data: ensemble = [] } = useGetEnsembleQuery(data.prod_num);
  const { data: planogrammeProduits = [] } = useGetPlanogrammeProduitsQuery(
    data.plano_seq
  );
  const [updatePlanogramme] = useUpdatePlanogrammeMutation();
  const [errorDateEntree, setErrorDateEntree] =
    React.useState<DateValidationError | null>(null);
  const [errorDateSortie, setErrorDateSortie] =
    React.useState<DateValidationError | null>(null);
  const [magValue, setMagValue] = useState<string>(
    magasins.find((e) => e.mag_num === data!.mag_num)
      ? magasins.find((e) => e.mag_num === data!.mag_num)!.mag_nom
      : ""
  );
  const [dateEntre, setDateEntre] = useState<any>(
    data?.plano_trsf_dt_entree !== null
      ? dayjs(data?.plano_trsf_dt_entree.replaceAll("-", "/"))
      : null
  );
  const [dateSorti, setDateSorti] = useState<any>(
    data?.plano_trsf_dt_sortie !== null
      ? dayjs(data?.plano_trsf_dt_sortie.replaceAll("-", "/"))
      : null
  );
  const [value, setValue] = useState<string>(
    data?.plano_prod_num_remp && data?.plano_prod_num_remp !== 0
      ? data?.plano_prod_num_remp.toString()
      : ""
  );
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [planoQuantite, setPlanoQuantite] = useState<number>(data.plano_qte);
  const [ensembleValues, setEnsembleValues] = useState<ProduitQuantite[]>(
    planogrammeProduits.map((e) => {
      return {
        prod_num: e.prod_num,
        plano_qte: e.plano_qte,
      };
    })
  );
  const d_inv01rp = useAppSelector((state) => state.appState.d_inv01rp);

  useEffect(() => {
    setUnsaved(isDirty);
  }, [isDirty, setUnsaved]);

  useEffect(() => {
    setEnsembleValues(
      planogrammeProduits.map((e) => {
        return {
          prod_num: e.prod_num,
          plano_qte: e.plano_qte,
        };
      })
    );
  }, [planogrammeProduits]);

  useEffect(() => {
    if (data) {
      setMagValue(
        magasins.find((e) => e.mag_num === data!.mag_num)
          ? magasins.find((e) => e.mag_num === data!.mag_num)!.mag_nom
          : ""
      );
      setDateEntre(
        data?.plano_trsf_dt_entree !== null
          ? dayjs(data?.plano_trsf_dt_entree.replaceAll("-", "/"))
          : null
      );
      setDateSorti(
        data?.plano_trsf_dt_sortie !== null
          ? dayjs(data?.plano_trsf_dt_sortie.replaceAll("-", "/"))
          : null
      );
      setValue(
        data?.plano_prod_num_remp && data?.plano_prod_num_remp !== 0
          ? data?.plano_prod_num_remp.toString()
          : ""
      );
      setIsDirty(false);
      setPlanoQuantite(data.plano_qte);
    }
  }, [data, magasins]);

  const submit = async () => {
    let datas = {
      mag_num: data?.mag_num,
      plano_seq: data?.plano_seq,
      prod_num: data?.prod_num,
      plans_seq: data?.plans_seq,
      plano_dt_deb: data?.plano_dt_deb,
      plano_dt_fin: data?.plano_dt_fin,
      plano_qte: planoQuantite,
      plano_trsf_dt_entree:
        dateEntre !== null
          ? typeof dateEntre == "string"
            ? dateEntre.replaceAll("-", "/")
            : dateEntre.format("YYYY/MM/DD")
          : null,
      plano_trsf_dt_sortie:
        dateSorti !== null
          ? typeof dateSorti == "string"
            ? dateSorti.replaceAll("-", "/")
            : dateSorti.format("YYYY/MM/DD")
          : null,
      plano_prod_num_remp: value,
      statut_aff: data?.statut_aff,
    };
    dispatch(
      addRequestToSend(
        new RequestToSend(
          `Modification planogramme ${data.magasin.mag_nom}`,
          updatePlanogramme,
          (result) => {
            if (ensemble.length > 0)
              dispatch(
                addRequestToSend(
                  new RequestToSend(
                    `Sauvegarde produit/ensemble ${data.magasin.mag_nom}`,
                    updatePlanogrammeProduits,
                    (result) => {
                      if (!result.status) setIsDirty(false);
                    },
                    { planoSeq: data.plano_seq },
                    ensembleValues,
                    ["planogrammeProduits"]
                  )
                )
              );
            else if (!result.status) setIsDirty(false);
          },
          {},
          datas,
          []
        )
      )
    );
  };

  const handleDelete = async () => {
    if (window.confirm("Voulez vous vraiment supprimer le planogramme?")) {
      dispatch(
        addRequestToSend(
          new RequestToSend(
            `Supprimer planogramme ${data.magasin.mag_nom}`,
            deletePlanogramme,
            () => {},
            {},
            { plano_seq: data?.plano_seq },
            []
          )
        )
      );
    }
  };

  const handleCardClick = (ptr: number) => {
    setValue(padZerosLeft(ptr, PTR_LENGTH));
    setOpen(false);
  };

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title="PTR"
        style={getModalStyle}
      >
        <PTRModal handleCardClick={handleCardClick} />
      </GenericModal>
      <GenericModal
        open={showModalPlanoProduits}
        handleClose={() => setShowModalPlanoProduits(false)}
        title="Produits inclus"
        style={getModalStyle}
      >
        <PlanoProduitsModal
          prodNum={data.prod_num}
          ensembleValues={ensembleValues}
          closeModal={() => setShowModalPlanoProduits(false)}
          update={(values: ProduitQuantite[]) => {
            setEnsembleValues(values);
            setIsDirty(true);
          }}
        />
      </GenericModal>
      <Box sx={{ display: "flex" }}>
        <Box display={"flex"} flexDirection={"column"}>
          <Box display={"flex"}>
            {data?.mag_num && (
              <TextField
                value={magValue}
                disabled
                sx={{ m: 1, width: 200, flexShrink: 0 }}
              />
            )}

            <TextField
              label="Quantité"
              type="number"
              sx={{ width: "75px", m: 1 }}
              InputLabelProps={{
                shrink: true,
              }}
              value={planoQuantite}
              InputProps={{ inputProps: { min: 1, max: 100 } }}
              onChange={(e) => {
                if (!isDirty) setIsDirty(true);
                if (e.target.value !== "") {
                  setPlanoQuantite(Number(e.target.value));
                }
              }}
              disabled={ensemble.length > 0}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />

            <Tooltip
              placement={"bottom"}
              title={
                "Date demandée pour que le démo plancher soit transféré vers le plancher"
              }
            >
              <div>
                <DatePicker
                  disabled={listChecked.length > 0 ? true : false}
                  onAccept={(newDate) => {
                    setDateEntre(newDate);
                  }}
                  value={dateEntre}
                  label="Date d'entrée"
                  onChange={(event) => {
                    setIsDirty(true);
                    setDateEntre(event ? event.format("YYYY/MM/DD") : null);
                  }}
                  sx={{ m: 1, width: 200, flexShrink: 0 }}
                  onError={(newError) => setErrorDateEntree(newError)}
                  slotProps={{
                    textField: {
                      helperText:
                        errorDateEntree === "invalidDate"
                          ? "Le format est invalide"
                          : "",
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  format="DD/MM/YYYY"
                />
              </div>
            </Tooltip>
            <Tooltip
              placement={"bottom"}
              title={
                "Date demandée pour que le démo plancher soit transféré hors du plancher"
              }
            >
              <div>
                <DatePicker
                  disabled={listChecked.length > 0 ? true : false}
                  onAccept={(newDate) => {
                    setDateSorti(newDate);
                  }}
                  value={dateSorti}
                  label="Date de sortie"
                  onChange={(event) => {
                    setIsDirty(true);
                    setDateSorti(event ? event.format("YYYY/MM/DD") : null);
                  }}
                  sx={{ m: 1, width: 200, flexShrink: 0 }}
                  onError={(newError) => setErrorDateSortie(newError)}
                  slotProps={{
                    textField: {
                      helperText:
                        errorDateSortie === "invalidDate"
                          ? "Le format est invalide"
                          : "",
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  format="DD/MM/YYYY"
                />
              </div>
            </Tooltip>
            <TextField
              disabled={listChecked.length > 0 ? true : false}
              type="search"
              label="Remplace le"
              value={value ? padZerosLeft(value, PTR_LENGTH) : ""}
              onBlur={() => {
                if (value) setValue(padZerosLeft(value!, PTR_LENGTH));
              }}
              onChange={(event) => {
                setIsDirty(true);
                setValue(event.target.value);
              }}
              sx={{
                m: 1,
                width: 200,
                color: "#233044",
                background: "white",
                flexShrink: 0,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => setOpen(true)}
                      disabled={listChecked.length > 0}
                    >
                      <SearchIcon />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {ensemble.length > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                flexShrink: 0,
              }}
            >
              <TextField
                disabled={listChecked.length > 0}
                type="search"
                label="Produits inclus"
                value={
                  ensembleValues
                    .map((e) => padZerosLeft(e.prod_num, PTR_LENGTH))
                    .join(", ") || ""
                }
                onChange={(event) => {
                  setIsDirty(true);
                }}
                sx={{
                  m: 1,
                  color: "#233044",
                  background: "white",
                  flexShrink: 0,
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowModalPlanoProduits(true)}
                        disabled={listChecked.length > 0}
                      >
                        <EditIcon />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
        <Tooltip title="Enregistrer">
          <span
            style={{
              display: "flex",
              alignItems: "center",
              padding: "6px 28px",
              cursor: "pointer",
            }}
            className={`save-wrapper ${
              isDirty
                ? Boolean(errorDateEntree) || Boolean(errorDateSortie)
                  ? "custom-disabled"
                  : ""
                : "custom-disabled"
            }`}
            onClick={submit}
          >
            <span className={`k-icon k-font-icon k-i-save k-icon-xl`}></span>
          </span>
        </Tooltip>
        <Tooltip title="Effacer">
          <span style={{ display: "flex" }}>
            <Button
              onClick={handleDelete}
              color="error"
              disabled={
                !d_inv01rp.canDelete || listChecked.length > 0 ? true : false
              }
            >
              {" "}
              <DeleteIcon />
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
}

export default React.memo(FormEditMagasinProduit);
